import React, { useMemo } from 'react';
import { TypedMemo } from '../common/types';
import { LoadingView } from '../common/views/LoadingView';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { useTeam } from './useTeam';
import { TeamMembersList } from './TeamMembersList';
import { TeamInviteForm } from './TeamInviteForm';
import { TeamPendingInvites } from './TeamPendingInvites';
import { TeamLeaveCard } from './TeamLeaveCard';
import { WorkspaceNameForm } from '../workspaces/WorkspaceNameForm';
import { YourWorkspacesCard } from '../workspaces/YourWorkspacesCard';
import { MemberWorkspacePanel } from '../workspaces/MemberWorkspacePanel';
import { MembersSeeEarningsPanel } from '../workspaces/MembersSeeEarningsPanel';
import { useDefaultWorkspaceName } from '../workspaces/useDefaultWorkspaceName';

const TeamTabComponent: React.FC = () => {
  const team = useTeam();
  const members = team.data?.members;
  const ownerEmail = useMemo(() => members?.find((member) => member.role === 'OWNER')?.email ?? null, [members]);
  const defaultName = useDefaultWorkspaceName(ownerEmail);

  if (team.isLoading) {
    return <LoadingView />;
  }
  if (team.error) {
    return <ErrorView error={team.error} retry={team.refetch} />;
  }
  if (!team.data) {
    return <ErrorView retry={team.refetch} />;
  }

  const { data } = team;
  const isOwner = data.myRole === 'OWNER';
  // Same count as the server's `countSeatsInUse`: members plus invites that can still be accepted.
  const seatsLeft =
    data.seatsLimit === null ? null : Math.max(0, data.seatsLimit - data.members.length - data.pendingInvites.length);

  return (
    <div>
      {/* The page head (SettingsPageHead) shows the title "Team" and the description. */}
      {/* design: workspaces d1: the owner names the active workspace. */}
      {isOwner && <WorkspaceNameForm customName={data.customWorkspaceName} defaultName={defaultName} />}
      {/* design: your-intelligence e4: the owner decides if members see the earnings. */}
      {isOwner && <MembersSeeEarningsPanel />}
      {/* design: workspaces e1: a member sees the workspace read only. */}
      {!isOwner && <MemberWorkspacePanel />}

      <TeamMembersList
        members={data.members}
        myUserId={data.myUserId}
        canRemove={isOwner}
        onRemove={(userId) => team.removeMember.mutateAsync({ userId })}
        removeError={team.removeMember.error}
        seatsLimit={data.seatsLimit}
        pendingInvitesCount={data.pendingInvites.length}
        isRemoving={team.removeMember.isPending}
      />

      {isOwner && (
        <>
          <TeamInviteForm invite={team.invite} seatsLeft={seatsLeft} workspaceName={data.workspaceName} />
          <TeamPendingInvites
            invites={data.pendingInvites}
            onRevoke={(inviteId) => team.revokeInvite.mutate({ inviteId })}
            revokeError={team.revokeInvite.error}
            isRevoking={team.revokeInvite.isPending}
          />
        </>
      )}

      {/* design: workspaces e1 / e3: no Leave on the home workspace of an old-model member (D8). */}
      {!isOwner && !data.isHome && <TeamLeaveCard workspaceName={data.workspaceName} licenseId={data.licenseId} />}
      <YourWorkspacesCard />
    </div>
  );
};

export const TeamTab = TypedMemo(TeamTabComponent);
